export class ProgramSpoiler {
  constructor(el) {
    this.details = el
    this.content = el.querySelector('.program-spoiler__content')

    if (!this.content) {
      return
    }

    this.animation = null
    this.isClosing = false
    this.isExpanding = false

    this.summary = el.querySelector('.program-spoiler__summary')
    this.chevron = this.summary.querySelector('.ui-animated-chevron')
    this.subtitleText = el.querySelector('.program-spoiler__subtitle-text')

    this.summary.addEventListener('click', evt => this._clickHandler(evt))
  }

  _clickHandler(evt) {
    evt.preventDefault()

    if (this.details.dataset.disabled === 'true') {
      return
    }

    const isSupportingAnimate = typeof (this.details.animate) === 'function' // fix safari 1-13 animate

    this.details.style.overflow = 'hidden'

    const isOpen = this.details.open || this.details.dataset.open === 'true'

    if (this.isClosing || !isOpen) {
      if (isSupportingAnimate) {
        this._open()
      } else {
        this.details.open = true
      }

      this.chevron.classList.add('ui-animated-chevron--active')

      this._pushAnalytics()
    } else if (this.isExpanding || isOpen) {
      if (isSupportingAnimate) {
        this._shrink()
      } else {
        this.details.open = false
      }

      this.chevron.classList.remove('ui-animated-chevron--active')
    }
  }

  _shrink() {
    this.isClosing = true

    const subtitleMetrics = this._getSubtitleMetrics()
    const startHeight = `${this.details.offsetHeight}px`
    const subtitleDelta = this.subtitleText && subtitleMetrics.isCollapsible
      ? this.subtitleText.offsetHeight - subtitleMetrics.collapsedHeight
      : 0
    const endHeight = `${this.summary.offsetHeight - subtitleDelta}px`

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 300,
      easing: 'ease-out'
    })

    this.content.animate({
      opacity: [1, 0]
    }, {
      duration: 200,
      easing: 'ease-in-out',
      fill: 'forwards'
    })

    if (this.subtitleText && subtitleMetrics.isCollapsible) {
      const currentH = `${this.subtitleText.offsetHeight}px`
      this.subtitleText.classList.add('program-spoiler__subtitle-text--animating')
      this.subtitleText.animate({
        maxHeight: [currentH, `${subtitleMetrics.collapsedHeight}px`]
      }, {
        duration: 300,
        easing: 'ease-out',
        fill: 'forwards'
      })
    }

    this.animation.onfinish = () => this._finishAnimation(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  _open() {
    this.details.style.height = `${this.details.offsetHeight}px`
    this.details.open = true

    if (this.subtitleText) {
      this.subtitleText.style.webkitLineClamp = 'unset'
    }

    window.requestAnimationFrame(() => this._expand())
  }

  _expand() {
    this.isExpanding = true

    const subtitleMetrics = this._getSubtitleMetrics()
    const startHeight = `${this.details.offsetHeight}px`
    const subtitleDelta = this.subtitleText && subtitleMetrics.isCollapsible
      ? subtitleMetrics.fullHeight - subtitleMetrics.collapsedHeight
      : 0
    const endHeight = `${this.summary.offsetHeight + subtitleDelta + this.content.offsetHeight}px`

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 300,
      easing: 'ease-out'
    })

    this.content.animate({
      opacity: [0, 1]
    }, {
      duration: 200,
      easing: 'ease-in-out',
      fill: 'forwards'
    })

    if (this.subtitleText && subtitleMetrics.isCollapsible) {
      const fullH = `${subtitleMetrics.fullHeight}px`
      this.subtitleText.classList.add('program-spoiler__subtitle-text--animating')
      this.subtitleText.animate({
        maxHeight: [`${subtitleMetrics.collapsedHeight}px`, fullH]
      }, {
        duration: 300,
        easing: 'ease-out',
        fill: 'forwards'
      })
    }

    this.animation.onfinish = () => this._finishAnimation(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  _finishAnimation(open) {
    this.details.open = open

    this.animation = null
    this.isClosing = false
    this.isExpanding = false

    this.details.style.height = 'auto'
    this.details.style.overflow = ''

    if (this.subtitleText) {
      this.subtitleText.style.maxHeight = open ? 'none' : '4em'
      this.subtitleText.style.webkitLineClamp = open ? 'unset' : '3'
      this.subtitleText.classList.remove('program-spoiler__subtitle-text--animating')
    }
  }

  _getSubtitleMetrics() {
    if (!this.subtitleText) {
      return {
        collapsedHeight: 0,
        fullHeight: 0,
        isCollapsible: false
      }
    }

    const initialMaxHeight = this.subtitleText.style.maxHeight
    const initialLineClamp = this.subtitleText.style.webkitLineClamp

    // Временно переключаем состояния subtitle, чтобы измерить актуальную высоту после ресайза.
    this.subtitleText.style.maxHeight = 'none'
    this.subtitleText.style.webkitLineClamp = 'unset'
    const fullHeight = this.subtitleText.scrollHeight

    this.subtitleText.style.maxHeight = '4em'
    this.subtitleText.style.webkitLineClamp = '3'
    const collapsedHeight = this.subtitleText.offsetHeight

    this.subtitleText.style.maxHeight = initialMaxHeight
    this.subtitleText.style.webkitLineClamp = initialLineClamp

    return {
      collapsedHeight,
      fullHeight,
      isCollapsible: fullHeight - collapsedHeight > 1
    }
  }

  _pushAnalytics() {
    const detailSpoilersGtmName = this.details.closest('[data-gtm]')

    if (detailSpoilersGtmName) {
      const gtmAttribute = detailSpoilersGtmName.dataset.gtm
      const itemText = this.summary.textContent.replace(/\s+/g, ' ').trim()

      const eventName = `${gtmAttribute}-accordion-click`
      window.$analytics && window.$analytics.push(eventName, { text: itemText })
    }
  }
}

export const ProgramSpoilerInit = () => {
  const detailSpoilers = document.querySelectorAll('.program-spoiler')

  detailSpoilers.forEach(detailSpoiler => new ProgramSpoiler(detailSpoiler))
}
